import React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@ideoz/ui';
import type { DesignSystem } from './DesignSystemPanel';
import { CORNER_RADIUS_PILLS } from '../designSystemConstants';

const PREVIEW_RADIUS: Record<NonNullable<DesignSystem['borderRadius']>, string> = {
  none: '0px',
  sm: '2px',
  md: '6px',
  lg: '12px',
  full: '9999px',
};

export type CornerRadiusSelectorProps = {
  value?: DesignSystem['borderRadius'];
  onChange: (value: NonNullable<DesignSystem['borderRadius']>) => void;
  className?: string;
};

export function CornerRadiusSelector({
  value,
  onChange,
  className,
}: CornerRadiusSelectorProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className={className}>
      <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
        {t('designSystem.cornerRadius')}
      </p>
      <div className="grid grid-cols-4 gap-3">
        {CORNER_RADIUS_PILLS.map((opt) => {
          const selected = value === opt.value;
          const previewRadius = PREVIEW_RADIUS[opt.value];

          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange(opt.value)}
              aria-pressed={selected}
              className={cn(
                'flex flex-col items-center gap-2 rounded-xl border bg-white px-3 py-4 transition-colors dark:bg-gray-900',
                selected
                  ? 'border-blue-400 dark:border-blue-500/50 bg-blue-50/50 dark:bg-blue-500/10'
                  : 'border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600'
              )}
            >
              <div
                className="h-5 w-full border border-primary-200 bg-primary-100 dark:border-primary-500/30 dark:bg-primary-500/15"
                style={{ borderRadius: previewRadius }}
              />
              <span
                className={cn(
                  'text-sm font-medium',
                  selected
                    ? 'text-primary-700 dark:text-primary-300'
                    : 'text-gray-700 dark:text-gray-300'
                )}
              >
                {t(`designSystem.opt.cornerRadius.${opt.value}`, { defaultValue: opt.label })}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default CornerRadiusSelector;
